import { create } from 'zustand';
import type { SupportConfigDto, TicketDto, TicketSummaryDto } from '@ecom/contracts';
import { useUiStore } from '@/i18n';
import { adminApi, api, errorMessage, withSession } from '@/lib/api-client';
import { settleTicket } from '@/lib/queue-rows';
import { useWorkspaceStore } from './workspace.store';

/**
 * The support queue (ADR-028).
 *
 * Every mutation returns the whole ticket, so the open thread re-renders from
 * one object rather than guessing what a reply did to its status — and its row
 * in the queue follows it, rather than the whole queue being read again.
 */

export interface TicketFilters {
  search: string;
  statuses: string[];
  /** A code from the store's reasons. */
  reason: string;
  assignedToId: string;
  /** '' any, 'true' only what nobody has picked up. */
  unassigned: string;
  from: string;
  to: string;
}

export const EMPTY_TICKET_FILTERS: TicketFilters = {
  search: '',
  statuses: [],
  reason: '',
  assignedToId: '',
  unassigned: '',
  from: '',
  to: '',
};

interface SupportState {
  items: TicketSummaryDto[];
  current: TicketDto | null;
  /** The ticket being opened — the thread shows its outline until it lands. */
  opening: string | null;
  nextCursor: string | null;
  total: number | null;
  loading: boolean;
  loadingMore: boolean;
  saving: boolean;
  error: string | null;
  filters: TicketFilters;
  /**
   * The reasons a customer chooses from, in the active language — the reason
   * filter's options. The store edits them (support settings), so they are
   * read, never listed here.
   */
  reasons: SupportConfigDto['reasons'];

  load: (filters?: Partial<TicketFilters>) => Promise<void>;
  loadMore: () => Promise<void>;
  loadReasons: () => Promise<void>;
  open: (id: string) => Promise<void>;
  close: () => void;
  reply: (id: string, body: string, isInternal: boolean) => Promise<boolean>;
  update: (
    id: string,
    patch: { status?: string; priority?: string; assignedToId?: string | null },
  ) => Promise<boolean>;
  /** The AI assistant's draft of the next reply, for a person to read and send. Throws when it cannot. */
  draftReply: (id: string) => Promise<string>;
}

const PAGE_SIZE = 20;

export const useSupportStore = create<SupportState>((set, get) => ({
  items: [],
  current: null,
  opening: null,
  nextCursor: null,
  total: null,
  loading: false,
  loadingMore: false,
  saving: false,
  error: null,
  filters: EMPTY_TICKET_FILTERS,
  reasons: [],

  async load(filters) {
    const next = { ...get().filters, ...filters };
    set({ loading: true, error: null, filters: next });

    try {
      const page = await withSession(() => adminApi.tickets.list(toQuery(next, PAGE_SIZE)));
      set({
        items: page.items,
        nextCursor: page.meta.nextCursor,
        total: page.meta.total ?? null,
        loading: false,
      });
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load the tickets.') });
    }
  },

  async loadMore() {
    const { nextCursor, loadingMore, filters, items } = get();
    if (!nextCursor || loadingMore) return;

    set({ loadingMore: true });

    try {
      const page = await withSession(() =>
        adminApi.tickets.list({ ...toQuery(filters, PAGE_SIZE), cursor: nextCursor }),
      );
      set({
        items: [...items, ...page.items],
        nextCursor: page.meta.nextCursor,
        loadingMore: false,
      });
    } catch (error) {
      set({ loadingMore: false, error: errorMessage(error, 'Could not load more tickets.') });
    }
  },

  async loadReasons() {
    try {
      // The contact form's own source (ADR-015), so the filter offers exactly
      // what a customer could have picked, worded as the store words it.
      const config = await withSession(() => api.catalog.supportConfig(useUiStore.getState().locale));
      set({ reasons: config.reasons });
    } catch {
      // Without them the filter is not offered; the queue itself still works.
    }
  },

  async open(id) {
    set({ opening: id, error: null });

    try {
      const ticket = await withSession(() => adminApi.tickets.get(id));
      // The ticket picked last is the one shown, whichever answer lands first.
      if (get().opening === id) set({ current: ticket, opening: null });
    } catch (error) {
      if (get().opening === id) set({ opening: null, error: errorMessage(error, 'Could not open the ticket.') });
    }
  },

  close() {
    set({ current: null, opening: null });
  },

  reply: (id, body, isInternal) =>
    mutate(set, get, () => adminApi.tickets.reply(id, { body, isInternal })),

  update: (id, patch) => mutate(set, get, () => adminApi.tickets.update(id, patch)),

  async draftReply(id) {
    const draft = await withSession(() => adminApi.ai.ticketReply(id));
    return draft.fields.body?.text ?? '';
  },
}));

/** Runs a mutation, keeps the open thread, and moves its row in the queue behind it. */
async function mutate(
  set: (partial: Partial<SupportState>) => void,
  get: () => SupportState,
  call: () => Promise<TicketDto>,
): Promise<boolean> {
  set({ saving: true, error: null });

  try {
    const ticket = await withSession(call);
    set({
      saving: false,
      // An agent who moved on while this saved keeps the ticket they moved to.
      current: get().current?.id === ticket.id ? ticket : get().current,
      // Answered goes to the top, as the queue is ordered; anything else stays put.
      items: settleTicket<TicketSummaryDto>(get().items, ticket),
    });
    // The menu's count of open tickets may have just changed.
    void useWorkspaceStore.getState().refreshAttention();
    return true;
  } catch (error) {
    set({ saving: false, error: errorMessage(error, 'Could not save.') });
    return false;
  }
}

function toQuery(filters: TicketFilters, limit: number) {
  return {
    limit,
    search: filters.search || undefined,
    status: filters.statuses.length > 0 ? filters.statuses : undefined,
    reason: filters.reason || undefined,
    assignedToId: filters.assignedToId || undefined,
    unassigned: filters.unassigned === 'true' ? true : undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
  };
}
